import React, { useState, useEffect } from 'react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import UserSelect from '../../components/UserSelect';
import api from '../../utils/api';
import { userFacingDetail } from '../../utils/errorHandler';
import { todayJST } from '../../utils/dateFormatter';

const CreateMeetingModal = ({
    isOpen,
    onClose,
    opportunityId,
    currentUser,
    onSuccess
}) => {
    const [newMeetingName, setNewMeetingName] = useState('');
    const [newOwnerName, setNewOwnerName] = useState('');
    const [newMeetingDate, setNewMeetingDate] = useState(todayJST());
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);

    useBodyScrollLock(isOpen);

    // モーダルが開かれた時に初期値を設定
    useEffect(() => {
        if (isOpen) {
            setNewMeetingName('');
            setNewOwnerName(currentUser ? currentUser.id : '');
            setNewMeetingDate(todayJST());
            setModalError(null);
        }
    }, [isOpen, currentUser]);

    const handleCreateMeeting = async (event) => {
        event.preventDefault();
        if (!newMeetingName || !newOwnerName) {
            setModalError('活動名と担当者は必須です。');
            return;
        }
        // 空のまま送るとサーバが 422（英語の検査の文言）を返すため、送る前に止める
        if (!newMeetingDate) {
            setModalError('活動日は必須です。');
            return;
        }
        setModalLoading(true);
        setModalError(null);
        try {
            const response = await api.fetch(`/meetings/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newMeetingName,
                    owner_id: newOwnerName,
                    opportunity_id: opportunityId,
                    meeting_date: newMeetingDate
                }),
            });
            if (!response) return; // Redirected
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(userFacingDetail(response.status, errorData) || '作成に失敗しました。');
            }
            const newMeeting = await response.json();
            setModalLoading(false);
            
            if (onSuccess) {
                onSuccess(newMeeting);
            }
            onClose();
        } catch (err) {
            setModalLoading(false);
            setModalError(err.message);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <form onSubmit={handleCreateMeeting}>
                    <h3 className="modal-title">新規活動追加</h3>
                    <div className="form-group">
                        <label htmlFor="meetingName">活動名 * (例: 1回目面談)</label>
                        <input
                            id="meetingName" type="text" value={newMeetingName}
                            onChange={(e) => setNewMeetingName(e.target.value)}
                            disabled={modalLoading}
                            autoFocus
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="ownerName">担当者 *</label>
                        <UserSelect
                            id="ownerId"
                            value={newOwnerName}
                            onChange={(val) => setNewOwnerName(val)}
                            placeholder="担当者を選択..."
                            disabled={modalLoading}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="meetingDate">活動日 *</label>
                        <input
                            id="meetingDate" type="date" value={newMeetingDate}
                            onChange={(e) => setNewMeetingDate(e.target.value)}
                            disabled={modalLoading}
                        />
                    </div>
                    {modalError && (
                        <p className="modal-error-message">{modalError}</p>
                    )}
                    <div className="modal-actions">
                        <button
                            type="button" className="modal-button-cancel"
                            onClick={onClose} disabled={modalLoading}
                        >
                            キャンセル
                        </button>
                        <button
                            type="submit" className="modal-button-confirm"
                            disabled={modalLoading}
                        >
                            {modalLoading ? '作成中...' : '追加'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateMeetingModal;
